import { useSyncExternalStore } from 'react';

/**
 * Preferencias de presentación del usuario: tema y modo de superficie.
 *
 * Viven en el almacenamiento del navegador y NO en el servidor: son de este
 * dispositivo. El mismo médico usa una tablet en el consultorio con luz y un
 * portátil en el turno de noche, y no querría la misma respuesta en los dos.
 *
 * Todo acceso va con guarda. El almacenamiento puede estar bloqueado —modo
 * privado, política del navegador, permisos del equipo— y lanzar al leerlo. Un
 * fallo de preferencia no puede impedir que la aplicación arranque.
 */

export type Tema = 'claro' | 'oscuro';

const CLAVE_TEMA = 'nat-tema';
const CLAVE_VIDRIO = 'nat-vidrio';

/** Lo que se asume antes de saber. Coincide con el `data-tema` del documento. */
const TEMA_POR_DEFECTO: Tema = 'claro';

/**
 * Un cambio hecho en ESTA pestaña no dispara el evento `storage`, que solo
 * avisa a las demás. Sin este evento propio, el conmutador cambiaría el tema y
 * no se enteraría de su propio cambio.
 */
const EVENTO = 'nat-preferencias';

function hayNavegador(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * `matchMedia` no siempre está. Falta en entornos de prueba, en navegadores
 * embebidos antiguos y en algunos visores de aplicación. Sin esta guarda, la
 * ausencia de una consulta de medios tumbaría la interfaz entera al leer una
 * preferencia — que es lo más barato del arranque.
 */
function consultaDeTemaOscuro(): MediaQueryList | null {
  if (!hayNavegador() || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia('(prefers-color-scheme: dark)');
}

function leerGuardado(clave: string): string | null {
  try {
    return localStorage.getItem(clave);
  } catch {
    return null;
  }
}

function guardar(clave: string, valor: string): void {
  try {
    localStorage.setItem(clave, valor);
  } catch {
    // La preferencia no persiste; la sesión actual sí la respeta.
  }
  window.dispatchEvent(new Event(EVENTO));
}

/**
 * Leer y guardar una preferencia cualquiera de ESTE navegador.
 *
 * Existen porque la guarda es lo que no se puede repetir: el almacenamiento
 * puede estar bloqueado —modo privado, política del navegador, permisos del
 * equipo— y **lanzar al leerlo**. Escrita otra vez en cada pantalla, la que se
 * olvide de envolverla tumba la aplicación entera por una preferencia, que es
 * lo más barato que hay.
 *
 * ⚠️ **LO QUE SE GUARDE AQUÍ SOBREVIVE AL CIERRE DE SESIÓN** y no se borra al
 * salir: en una estación compartida, lo que dejó una persona lo lee la
 * siguiente. Estas dos claves guardan cómo quiere alguien ver la pantalla y
 * nada más, y esa es la regla por defecto — **guardar algo que venga de una
 * persona es una decisión que hay que tomar a sabiendas y dejar escrita**, con
 * su fecha de revisión. Hoy hay exactamente una, y tiene ADR:
 *
 *   · el **usuario recordado** de la puerta —ADR-0013—, que es un documento de
 *     identidad y se guarda sólo si alguien marca la casilla. La contraseña
 *     **no**, y eso no es un pendiente: es la decisión.
 *
 * Eran dos hasta el 30/09/2026: las **vistas guardadas** de la matriz vivían
 * aquí —ADR-0012— y ese día pasaron a la base, por persona (ADR-0015). Lo que
 * quedara en un navegador lo borra `olvidarLoRetirado`, abajo.
 *
 * La clave lleva `nat-` delante para no chocar con nada que comparta origen.
 */
export function leerPreferencia(clave: string): string | null {
  if (!hayNavegador()) return null;
  return leerGuardado(clave);
}

export function guardarPreferencia(clave: string, valor: string): void {
  if (!hayNavegador()) return;
  guardar(clave, valor);
}

/**
 * Olvidar una preferencia.
 *
 * Existe por lo mismo que las dos de arriba —la guarda no se puede repetir— y
 * porque **borrar no es guardar la cadena vacía**: una clave con valor vacío
 * sigue ahí, se sigue leyendo y obliga a cada lector a distinguir «no hay» de
 * «hay, y está vacío». Lo que se olvida se va.
 *
 * Avisa por el mismo evento que `guardar`: quien esté escuchando una
 * preferencia tiene que enterarse igual de que dejó de existir.
 */
export function borrarPreferencia(clave: string): void {
  if (!hayNavegador()) return;
  try {
    localStorage.removeItem(clave);
  } catch {
    // El almacén está bloqueado; no había nada que borrar que fuera a durar.
  }
  window.dispatchEvent(new Event(EVENTO));
}

/**
 * Lo que este sistema guardó en el navegador y YA NO GUARDA · 🆕 30/09/2026.
 *
 * Una clave que dejó de escribirse no desaparece sola: se queda en cada
 * navegador donde alguien la usó, y **sobrevive al cierre de sesión**. La de
 * las vistas de la matriz puede llevar dentro un apellido o un DNI escritos en
 * una búsqueda, y Alex decidió el 30/09 que esas vistas no se suben a la base:
 * se empieza de cero y **se borran**.
 *
 * La lista vive aquí, junto a las que se guardan, porque es la misma pregunta
 * —qué deja este sistema en el navegador de alguien—, y se borra al arrancar
 * CUALQUIERA de las aplicaciones: quien usaba la matriz puede no volver a
 * Operación, y su estación seguiría guardando lo que buscó.
 */
const CLAVES_RETIRADAS: readonly string[] = ['nat-vistas-matriz'];

/**
 * Borra de este navegador lo que el sistema ya no guarda. Sólo toca lo que está:
 * una clave que no existe no avisa a nadie de nada.
 */
export function olvidarLoRetirado(): void {
  if (!hayNavegador()) return;
  for (const clave of CLAVES_RETIRADAS) {
    if (leerGuardado(clave) !== null) borrarPreferencia(clave);
  }
}

export function leerTema(): Tema {
  if (!hayNavegador()) return TEMA_POR_DEFECTO;
  const guardado = leerGuardado(CLAVE_TEMA);
  if (guardado === 'claro' || guardado === 'oscuro') return guardado;
  // Sin preferencia explícita manda el sistema operativo, que ya sabe si es
  // de noche.
  return consultaDeTemaOscuro()?.matches === true ? 'oscuro' : 'claro';
}

export function fijarTema(tema: Tema): void {
  if (!hayNavegador()) return;
  document.documentElement.setAttribute('data-tema', tema);
  guardar(CLAVE_TEMA, tema);
}

/** `true` = superficies de vidrio · `false` = modo sólido. */
export function leerVidrio(): boolean {
  if (!hayNavegador()) return true;
  return leerGuardado(CLAVE_VIDRIO) !== 'no';
}

/**
 * El modo sólido sirve las mismas superficies con relleno macizo y sin
 * desenfoque. `backdrop-filter` lo repinta la tarjeta gráfica en cada cuadro,
 * y en un equipo de gama baja eso se nota; apagarlo conserva la identidad y
 * devuelve el rendimiento.
 */
export function fijarVidrio(activo: boolean): void {
  if (!hayNavegador()) return;
  if (activo) {
    document.documentElement.removeAttribute('data-vidrio');
    guardar(CLAVE_VIDRIO, 'si');
    return;
  }
  document.documentElement.setAttribute('data-vidrio', 'no');
  guardar(CLAVE_VIDRIO, 'no');
}

/**
 * Suscripción a los tres orígenes de cambio: esta pestaña, otra pestaña, y el
 * sistema operativo cuando el usuario no ha elegido tema.
 *
 * Ese tercero no es teórico: en un puesto médico que empalma turnos, el
 * sistema cambia a oscuro por la tarde y la interfaz debe seguirlo sin que
 * nadie recargue.
 */
function suscribir(alCambiar: () => void): () => void {
  if (!hayNavegador()) return () => undefined;

  const consulta = consultaDeTemaOscuro();
  window.addEventListener(EVENTO, alCambiar);
  window.addEventListener('storage', alCambiar);
  consulta?.addEventListener('change', alCambiar);

  return () => {
    window.removeEventListener(EVENTO, alCambiar);
    window.removeEventListener('storage', alCambiar);
    consulta?.removeEventListener('change', alCambiar);
  };
}

export interface Preferencias {
  readonly tema: Tema;
  readonly vidrio: boolean;
  readonly alternarTema: () => void;
  readonly alternarVidrio: () => void;
}

/**
 * Las preferencias son estado EXTERNO —almacenamiento del navegador y
 * preferencia del sistema—, no estado de React. Por eso se leen con
 * `useSyncExternalStore` y no con `useState` más un efecto de
 * sincronización: ese patrón provoca un render en cascada en cada montaje y
 * no se entera de los cambios que llegan de fuera.
 *
 * El tercer argumento es la instantánea del SERVIDOR. El portal se renderiza
 * ahí, donde no hay almacenamiento ni preferencia de sistema; devolver el
 * valor por defecto hace que los dos árboles coincidan y la hidratación no se
 * descarte.
 *
 * El destello no ocurre porque el tema real ya lo aplicó, antes del primer
 * pintado, el guion en línea del documento. Lo que se sincroniza aquí es solo
 * el estado de los conmutadores.
 */
export function usePreferencias(): Preferencias {
  const tema = useSyncExternalStore(suscribir, leerTema, () => TEMA_POR_DEFECTO);
  const vidrio = useSyncExternalStore(suscribir, leerVidrio, () => true);

  return {
    tema,
    vidrio,
    alternarTema: () => {
      fijarTema(tema === 'claro' ? 'oscuro' : 'claro');
    },
    alternarVidrio: () => {
      fijarVidrio(!vidrio);
    },
  };
}
